import { useState, useEffect } from 'react';
import { Video, Phone, Clock, Users, Calendar, ExternalLink, Copy, CheckCircle } from 'lucide-react';
import { useI18n } from '../i18n';

import { API_BASE } from '../config/api';

interface TelehealthAppointment {
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    status: string;
    dailyRoomUrl: string | null;
    dailyRoomName: string | null;
    chiefComplaint: string | null;
    patient: { id: string; firstName: string; lastName: string; mobilePhone: string };
    doctor: { id: string; firstName: string; lastName: string };
}

export function TelehealthPage() {
    const { t } = useI18n();
    const [appointments, setAppointments] = useState<TelehealthAppointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeRoom, setActiveRoom] = useState<string | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    useEffect(() => {
        loadTelehealthAppointments();
    }, []);

    async function loadTelehealthAppointments() {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/appointments?appointmentType=telehealth&limit=20`);
            const json = await res.json();
            setAppointments((json.data || []).filter((a: TelehealthAppointment) =>
                a.status !== 'cancelled' && a.status !== 'no_show'
            ));
        } catch (err) {
            console.error('Failed to load telehealth appointments:', err);
        } finally {
            setLoading(false);
        }
    }

    function copyLink(url: string, id: string) {
        navigator.clipboard.writeText(url);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    }

    const now = new Date();
    const upcoming = appointments.filter(a => new Date(a.scheduledStart) > now);
    const past = appointments.filter(a => new Date(a.scheduledStart) <= now);

    return (
        <div className="page-content fade-in">
            <div className="page-header" style={{ position: 'static', borderBottom: 'none', background: 'transparent', paddingLeft: 0 }}>
                <div>
                    <h1 className="page-title">{t('nav.telehealth')}</h1>
                    <p className="page-subtitle">Consultas por video — Daily.co</p>
                </div>
            </div>

            {/* Active Video Room */}
            {activeRoom && (
                <div className="card" style={{ marginBottom: 'var(--space-6)', border: '2px solid var(--color-primary)' }}>
                    <div className="card-header">
                        <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Video size={20} style={{ color: 'var(--color-primary)' }} /> Sala activa
                        </h2>
                        <button className="btn btn-danger btn-sm" onClick={() => setActiveRoom(null)}>Cerrar sala</button>
                    </div>
                    <div style={{
                        width: '100%', height: 500, borderRadius: 'var(--radius-lg)', overflow: 'hidden',
                        background: 'var(--color-surface-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <iframe
                            src={activeRoom}
                            allow="camera; microphone; fullscreen; display-capture"
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title="Teleconsulta"
                        />
                    </div>
                </div>
            )}

            {/* Stats */}
            <div className="stats-grid" style={{ marginBottom: 'var(--space-6)' }}>
                <div className="stat-card">
                    <div className="stat-icon stat-icon-primary"><Video size={24} /></div>
                    <div>
                        <div className="stat-value">{upcoming.length}</div>
                        <div className="stat-label">Próximas</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon stat-icon-success"><CheckCircle size={24} /></div>
                    <div>
                        <div className="stat-value">{past.filter(a => a.status === 'completed').length}</div>
                        <div className="stat-label">Completadas</div>
                    </div>
                </div>
            </div>

            {/* Upcoming Telehealth */}
            <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                <div className="card-header">
                    <h2 className="card-title">Próximas teleconsultas</h2>
                </div>
                {loading ? (
                    <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
                ) : upcoming.length === 0 ? (
                    <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                        <Video size={32} style={{ marginBottom: 8, opacity: 0.3 }} />
                        <p>No hay teleconsultas programadas</p>
                        <p style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>Las teleconsultas aparecerán aquí al agendar citas de tipo "Teleconsulta"</p>
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Fecha / Hora</th>
                                    <th>Paciente</th>
                                    <th>Motivo</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {upcoming.map(appt => (
                                    <tr key={appt.id}>
                                        <td style={{ fontWeight: 500 }}>
                                            <div>{new Date(appt.scheduledStart).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}</div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                                                {new Date(appt.scheduledStart).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </td>
                                        <td>
                                            <div>{appt.patient.firstName} {appt.patient.lastName}</div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{appt.patient.mobilePhone}</div>
                                        </td>
                                        <td style={{ color: 'var(--color-text-secondary)' }}>{appt.chiefComplaint || '—'}</td>
                                        <td><span className="badge badge-primary">{appt.status}</span></td>
                                        <td style={{ display: 'flex', gap: 4 }}>
                                            {appt.dailyRoomUrl ? (
                                                <>
                                                    <button className="btn btn-primary btn-sm" onClick={() => setActiveRoom(appt.dailyRoomUrl!)}>
                                                        <Video size={14} /> Iniciar
                                                    </button>
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={() => copyLink(appt.dailyRoomUrl!, appt.id)}
                                                        title="Copiar enlace"
                                                    >
                                                        {copiedId === appt.id ? <CheckCircle size={14} /> : <Copy size={14} />}
                                                    </button>
                                                </>
                                            ) : (
                                                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                                                    Sala pendiente
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Info Card */}
            <div className="card" style={{ background: 'var(--color-primary-bg, var(--color-surface-hover))' }}>
                <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
                    <Video size={24} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                    <div>
                        <p style={{ fontWeight: 600, marginBottom: 4 }}>Teleconsultas con Daily.co</p>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                            Las salas de video se crean automáticamente al agendar una cita de tipo "Teleconsulta".
                            El enlace se envía al paciente por WhatsApp. La consulta se graba para referencia clínica.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
